import React, { useMemo, useState } from 'react';
import { CheckCircle2, ListChecks, Loader2, Lock } from 'lucide-react';
import {
  useStageTaxonomy,
  matchKey,
  type CanonicalStage,
} from '../../../hooks/pipelineTracker/useStageTaxonomy';
import { suggestCanonical, WEAK_SUGGESTION } from '../../../hooks/pipelineTracker/stageSuggestion';
import type { UnmappedStageName } from '../../../hooks/pipelineTracker/usePipelineReport';
import { num } from './pipelineParts';

/**
 * Mapping the provider's stage names onto the stages the tracker reports on.
 *
 * WHY THIS IS INLINE AND NOT A SETTINGS PAGE. The list of values needing a
 * decision, the number of deals each one is hiding, and the coverage figure the
 * decision moves are all already on this panel. Sending an admin to a settings
 * screen would mean deciding without the count in front of them, which is the
 * one piece of context that makes the decision easy: 4 unmapped values covering
 * 900 deals is an incident, 40 covering 40 is housekeeping.
 *
 * WHAT SAVING DOES. Stages resolve at READ time, so a mapping saved here fixes
 * every snapshot already captured. Nothing is reprocessed, no stored row is
 * rewritten, and the deals rejoin the figures on the next read.
 *
 * WHAT IT DOES NOT DO. It never guesses on the tenant's behalf. The dropdown is
 * pre-filled with a suggestion and left UNSAVED, and "not mapped" stays a
 * first-class choice rather than a failure state, because a raw value nobody
 * can classify is better sitting visibly outside the figures than filed into
 * the wrong stage where it is invisible and wrong.
 */

export interface StageMappingPanelProps {
  companyId: string;
  /** Caller's role. Only super_admin and administrator may edit, mirroring TAXONOMY_ROLES. */
  canEdit: boolean;
  unmapped: UnmappedStageName[];
  /** Percentage of deals that currently resolve to a stage. */
  coveragePct: number;
  /** Called after a successful save so the report re-reads with the new mapping. */
  onMapped: () => void;
}

const CLASS_NOTE: Record<CanonicalStage['class'], string> = {
  active: 'in flight',
  lost: 'closed lost',
  post_settlement: 'after settlement',
};

const StageMappingPanel: React.FC<StageMappingPanelProps> = ({
  companyId,
  canEdit,
  unmapped,
  coveragePct,
  onMapped,
}) => {
  const taxonomy = useStageTaxonomy(companyId, canEdit);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, true>>({});
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * One row per raw value, not one per workflow occurrence.
   *
   * The same stage name appears in several Salestrekker workflows, and asking
   * an admin the identical question eight times is how a mapping exercise gets
   * abandoned half-finished. The row saved is wildcard-scoped, so it answers
   * all of them at once and stays overridable per workflow later.
   */
  const rows = useMemo(() => {
    const byRaw = new Map<string, { raw: string; count: number; workflows: Set<string> }>();
    for (const item of unmapped) {
      const key = matchKey(item.raw);
      const existing = byRaw.get(key);
      if (existing) {
        existing.count += item.count;
        existing.workflows.add(item.workflow);
      } else {
        byRaw.set(key, { raw: item.raw, count: item.count, workflows: new Set([item.workflow]) });
      }
    }
    // Most deals first: the decision worth making is the one hiding the most.
    return [...byRaw.values()].sort((a, b) => b.count - a.count);
  }, [unmapped]);

  const suggestions = useMemo(() => {
    const out: Record<string, { key: string; score: number } | undefined> = {};
    for (const row of rows) out[row.raw] = suggestCanonical(row.raw, taxonomy.canonical)[0];
    return out;
  }, [rows, taxonomy.canonical]);

  const hiddenDeals = rows.reduce((sum, row) => sum + row.count, 0);

  const onSave = async (raw: string, canonicalKey: string) => {
    setProblem(null);
    if (canonicalKey === '') {
      const outcome = await taxonomy.clearMapping(raw);
      if (!outcome.ok) setProblem(outcome.message ?? 'That change could not be saved.');
      else onMapped();
      return;
    }
    const outcome = await taxonomy.saveMapping(raw, canonicalKey);
    if (!outcome.ok) {
      setProblem(outcome.message ?? 'That change could not be saved.');
      return;
    }
    setSaved((prev) => ({ ...prev, [raw]: true }));
    onMapped();
  };

  if (rows.length === 0) return null;

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-200">
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500/[0.18] text-amber-400">
          <ListChecks className="h-4 w-4" />
        </span>
        Stages that need mapping
      </div>
      <p className="mb-3 text-xs text-gray-400">
        {coveragePct}% of deals resolve to a stage.{' '}
        {canEdit ? (
          <>
            Mapping one of these also fixes every snapshot already captured, with no reprocessing.
            The {num(hiddenDeals)} deals below rejoin the figures as soon as you choose a stage.
          </>
        ) : (
          <>
            The {num(hiddenDeals)} deals below sit outside every figure until a stage is chosen for
            them. Mapping changes what the whole company sees, so it is limited to an administrator.
          </>
        )}
      </p>

      {canEdit && taxonomy.error && (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          {taxonomy.error}
        </div>
      )}
      {problem && (
        <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          {problem}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const suggestion = suggestions[row.raw];
          const selected = choice[row.raw] ?? suggestion?.key ?? '';
          const busy = taxonomy.saving === row.raw;
          const weak = suggestion !== undefined && suggestion.score < WEAK_SUGGESTION;

          return (
            <div
              key={row.raw}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.03] px-3 py-2 text-sm transition-colors hover:bg-blue-500/[0.06]"
            >
              <div className="min-w-[14rem] flex-1">
                <span className="text-gray-200">{row.raw}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {row.workflows.size === 1
                    ? [...row.workflows][0]
                    : `${row.workflows.size} workflows`}
                </span>
              </div>

              <b className="tabular-nums text-amber-400">{num(row.count)}</b>

              {canEdit ? (
                <div className="flex items-center gap-2">
                  <select
                    aria-label={`Canonical stage for ${row.raw}`}
                    className="rounded-md border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 disabled:opacity-50"
                    value={selected}
                    disabled={busy || !taxonomy.ready}
                    onChange={(e) => {
                      setChoice((prev) => ({ ...prev, [row.raw]: e.target.value }));
                    }}
                  >
                    {/*
                      Leaving a value unmapped is a legitimate answer, not a
                      default nobody chose, so it is spelled out rather than
                      shown as an empty placeholder.
                    */}
                    <option value="">Leave unmapped (excluded from every figure)</option>
                    {taxonomy.canonical.map((stage) => (
                      <option key={stage.key} value={stage.key}>
                        {stage.label} ({CLASS_NOTE[stage.class]})
                      </option>
                    ))}
                  </select>
                  {/*
                    An EXPLICIT confirm, not save-on-change.

                    Two reasons. The dropdown arrives pre-filled with a
                    suggestion, and a select fires no change event for the value
                    already shown, so save-on-change made accepting the
                    suggestion literally impossible: the operator saw the right
                    answer selected, saw the unmapped count unchanged, and had
                    no action available. Found on stage, not in a test, because
                    every test drove the picker to a DIFFERENT value.

                    And a mapping changes every figure the whole company sees.
                    That deserves a deliberate click rather than firing on the
                    way past while someone is reading the options.
                  */}
                  <button
                    type="button"
                    disabled={busy || !taxonomy.ready || selected === ''}
                    onClick={() => void onSave(row.raw, selected)}
                    className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-40"
                  >
                    {saved[row.raw] ? 'Mapped' : 'Map'}
                  </button>
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />}
                  {!busy && saved[row.raw] && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  )}
                  {!busy && !saved[row.raw] && weak && (
                    <span
                      className="text-[0.625rem] uppercase tracking-wider text-gray-500"
                      title="This is a guess from the name alone. Check it before saving."
                    >
                      guess
                    </span>
                  )}
                </div>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Lock className="h-3 w-3" />
                  administrator only
                </span>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <p className="mt-3 text-xs text-gray-500">
          A mapping saved here applies in every workflow and to every snapshot, past and future.
          Choosing &ldquo;leave unmapped&rdquo; puts a value back outside the figures, where it is
          visible and fixable rather than silently counted in the wrong stage.
        </p>
      )}
    </div>
  );
};

export default StageMappingPanel;
